// src/components/MobileBottomNav.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import {
  FaHome,
  FaListUl,        // Bookings
  FaHeadset,       // Help
  FaRegUserCircle, // Account
} from "react-icons/fa";
// ✅ Use the path that exists in your repo (original was ../AuthContext)
import { useAuth } from "../AuthContext";

/** Keep this in sync with the bar height in Tailwind classes below */
const NAV_HEIGHT_PX = 64; // 16 * 4

const MobileBottomNav = () => {
  const auth = useAuth() || {};
  const {
    user,
    logout = () => {},
    token, // in case your hook provides it
    loading,
  } = auth;

  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const sheetRef = useRef(null);

  const isLoggedIn = !!user && !!token && !loading;
  const role = user?.role || "user";

  // hide bottom nav on selected pages
  const hiddenPaths = ["/download-ticket"];
  const shouldHideNav =
    hiddenPaths.includes(location.pathname) ||
    location.pathname.startsWith("/download-ticket");

  // close on route change
  useEffect(() => {
    setShowMenu(false);
  }, [location.pathname]);

  // close on ESC
  useEffect(() => {
    if (!showMenu) return;
    const onEsc = (e) => e.key === "Escape" && setShowMenu(false);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [showMenu]);

  const tabClasses = (active) =>
    [
      "w-full flex flex-col items-center justify-center",
      "h-16 text-[11px] font-medium",
      active ? "text-[#d84e55]" : "text-gray-600",
      "select-none",
    ].join(" ");

  const accountTabActive = location.pathname === "/account";

  const handleMenuAction = (action) => {
    setShowMenu(false);

    if (action === "profile") {
      navigate("/profile");
      return;
    }

    if (action === "operator") {
      navigate("/operator/dashboard");
      return;
    }

    if (action === "admin") {
      navigate("/admin");
      return;
    }

    if (action === "logout") {
      logout();
      navigate("/");
      return;
    }

    if (action === "login") {
      navigate("/login");
      return;
    }

    if (action === "signup") {
      navigate("/signup");
      return;
    }
  };

  if (shouldHideNav) return null;

  return (
    <>
      {/* --- Bottom navigation --- */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white shadow-[0_-4px_14px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        role="navigation"
        aria-label="Primary"
      >
        <ul className="grid grid-cols-4">
          {/* Home */}
          <li className="relative">
            <NavLink to="/" className={({ isActive }) => tabClasses(isActive)}>
              {() => (
                <>
                  <FaHome className="text-[24px]" />
                  <span className="mt-0.5">Home</span>
                </>
              )}
            </NavLink>
          </li>

          {/* Bookings (visible; route can auth-guard) */}
          <li className="relative">
            <NavLink
              to="/my-bookings"
              className={({ isActive }) => tabClasses(isActive)}
            >
              {() => (
                <>
                  <FaListUl className="text-[24px]" />
                  <span className="mt-0.5">Bookings</span>
                </>
              )}
            </NavLink>
          </li>

          {/* Help */}
          <li className="relative">
            <NavLink
              to="/help"
              className={({ isActive }) => tabClasses(isActive)}
            >
              {() => (
                <>
                  <FaHeadset className="text-[24px]" />
                  <span className="mt-0.5">Help</span>
                </>
              )}
            </NavLink>
          </li>

          {/* Account → opens Account page */}
          <li className="relative">
            <button
              type="button"
              onClick={() => navigate("/account")}
              className={tabClasses(accountTabActive)}
              aria-label="Account"
            >
              <FaRegUserCircle className="text-[24px]" />
              <span className="mt-0.5">Account</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* --- Account Bottom Sheet --- */}
      {false && showMenu && (
        <div
          className="fixed inset-0 z-[60] lg:hidden"
          role="dialog"
          aria-modal="true"
        >
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/35"
            onClick={() => setShowMenu(false)}
          />

          {/* sheet */}
          <div
            id="account-sheet"
            ref={sheetRef}
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl border-t border-gray-100 shadow-[0_-20px_40px_rgba(0,0,0,0.2)]"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            <div className="flex justify-center pt-3">
              <span className="h-1.5 w-12 rounded-full bg-gray-200" />
            </div>

            <div className="px-4 pt-3 pb-5">
              <p className="text-sm font-semibold text-gray-900">Account</p>

              {isLoggedIn ? (
                <>
                  <div className="mt-3 space-y-2">
                    <button
                      onClick={() => handleMenuAction("profile")}
                      className="w-full text-left px-4 py-3 rounded-xl border border-gray-100 hover:bg-gray-50 active:bg-gray-100"
                    >
                      <p className="text-sm font-medium text-gray-900">My Profile</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Manage your personal details
                      </p>
                    </button>

                    {role === "operator" && (
                      <button
                        onClick={() => handleMenuAction("operator")}
                        className="w-full text-left px-4 py-3 rounded-xl border border-gray-100 hover:bg-gray-50 active:bg-gray-100"
                      >
                        <p className="text-sm font-medium text-gray-900">
                          Operator Dashboard
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Manage buses and bookings
                        </p>
                      </button>
                    )}

                    {role === "admin" && (
                      <button
                        onClick={() => handleMenuAction("admin")}
                        className="w-full text-left px-4 py-3 rounded-xl border border-gray-100 hover:bg-gray-50 active:bg-gray-100"
                      >
                        <p className="text-sm font-medium text-gray-900">
                          Admin Dashboard
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Manage the platform
                        </p>
                      </button>
                    )}

                    <button
                      onClick={() => handleMenuAction("logout")}
                      className="w-full text-left px-4 py-3 rounded-xl border border-red-100 hover:bg-red-50 active:bg-red-100"
                    >
                      <p className="text-sm font-semibold text-red-600">Logout</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Sign out from this device
                      </p>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-3 space-y-2">
                    <button
                      onClick={() => handleMenuAction("login")}
                      className="w-full text-left px-4 py-3 rounded-xl border border-gray-100 hover:bg-gray-50 active:bg-gray-100"
                    >
                      <p className="text-sm font-medium text-gray-900">Log in</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Access bookings and profile
                      </p>
                    </button>

                    <button
                      onClick={() => handleMenuAction("signup")}
                      className="w-full text-left px-4 py-3 rounded-xl border border-gray-100 hover:bg-gray-50 active:bg-gray-100"
                    >
                      <p className="text-sm font-medium text-gray-900">Sign up</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Create your Routesbook account
                      </p>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* spacer so sheet doesn't clash with nav */}
            <div style={{ height: NAV_HEIGHT_PX }} />
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBottomNav;

